import axios from 'axios'
import fontFamilies from 'common/constants/fonts'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import { ContentBoxInterface } from 'common/types/entities/ContentBoxInterface'
import { LatestBlogPostsInterface } from 'common/types/entities/LatestBlogPostsInterface'
import { SearchResultsInterface } from 'common/types/entities/search-results-interface'
// @ts-expect-error @TODO unmaintained library with no ts support
import FontName from 'fontname'
import { googleFontsList } from 'src/shared/constants/google-fonts-list'
import { getNewBlogPostListingFontProperties } from 'src/shared/entities/blog-post-listing/utils/get-blog-post-listing-font-properties'
import { getButtonFontProperties as getNewButtonFontProperties } from 'src/shared/entities/button/utils/get-button-font-properties'
import { getContentBoxFontProperties } from 'src/shared/entities/content-box/utils/get-content-box-font-properties'
import { getContentTableFontProperties } from 'src/shared/entities/content-table/utils/get-content-table-font-properties'
import { FaqInterface } from 'src/shared/entities/faq/faq.schema'
import { getFaqFontProperties } from 'src/shared/entities/faq/utils/get-faq-font-properties'
import { FormInterface } from 'src/shared/entities/form/form.schema'
import { getFormFontProperties } from 'src/shared/entities/form/utils/get-form-font-properties'
import { getOfferPriceFontProperties } from 'src/shared/entities/offer-price/utils/get-offer-price-font-properties'
import { getSearchFontProperties } from 'src/shared/entities/search/utils/get-search-font-properties'
import { getSearchResultsFontProperties } from 'src/shared/entities/search-results/utils/get-search-results-font-properties'
import { getSurveyFontProperties } from 'src/shared/entities/survey/utils/get-survey-font-properties'
import { getTextFontProperties as getNewTextFontProperties } from 'src/shared/entities/text/utils/get-text-font-properties'
import { BaseEntityNewInterface } from 'src/shared/schemas/base-entity.schema'
import { CommonButtonEntityInterface } from 'src/shared/schemas/buttons/common-button-entity.schema'
import { DataFile } from 'src/shared/types/data-file-interface'
import { SearchInterface } from 'src/shared/types/entities/search-interface'
import { EntityNewInterfacesType } from 'src/shared/types/entity-new-interfaces-type'
import structureTypes from '../constants/structureTypes'
import { BreadcrumbsInterface } from '../types/entities/BreadcrumbsInterface'
import { ContentTableInterface } from '../types/entities/ContentTableInterface'
import EntityInterface, {
  BaseEntityInterface,
} from '../types/entities/EntityInterface'
import { OfferPriceInterface } from '../types/entities/OfferPriceInterface'
import { OldEntityInterface } from '../types/entities/OldEntityInterface'
import { SurveyInterface } from '../types/entities/SurveyInterface'
import { TextInterface } from '../types/entities/TextInterface'

export type GoogleFontProperties = {
  fontFamily: string // it's possible to have
  fontStyle?: string
  fontWeight?: string
}
export type CustomFontProperties = {
  fontFamily: string // it's possible to have
  fontFileId: number
}

export function createFontFace(fontFace: string) {
  const style = document.createElement('style')
  style.setAttribute('type', 'text/css')
  style.innerHTML = fontFace
  return style
}
// biome-ignore lint/suspicious/noExplicitAny: migrated from eslint
export function getBaseOldEntityFontProperties(entity: any) {
  const fontProperties = []
  if (entity?.styles?.fontFamily) {
    fontProperties.push({
      fontFamily: entity.styles.fontFamily,
      fontStyle: entity.styles.fontStyle,
      fontWeight: entity.styles.fontWeight,
    })
  }

  if (entity?.mobileStyles?.fontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileStyles.fontFamily,
      fontStyle: entity.mobileStyles.fontStyle,
      fontWeight: entity.mobileStyles.fontWeight,
    })
  }

  return fontProperties
}

// Legacy helpers for old button / order-bump / breadcrumbs entities.
//
// These implementations were copied from legacy client code to avoid `common` or
// `src/shared` depending on `client`:
// - client/components/entities/Button/index.js (getButtonFontProperties)
// - client/components/entities/OrderBump/index.js (getHeaderFontProperties)
// - client/pages/blog/entities/Breadcrumbs.tsx (getBreadcrumbsFontProperties)
//
// They are only used for `OldEntityInterface` cases in `getGoogleFontProperties`.
// New entities should use `src/shared/entities/*/utils` font helpers instead.
function getButtonFontProperties(entity: OldEntityInterface) {
  return [
    {
      fontFamily: entity.styles.fontFamily,
      fontStyle: entity.styles.fontStyle,
      fontWeight: entity.styles.fontWeight,
    },
    {
      fontFamily: entity.mobileStyles.fontFamily,
      fontStyle: entity.mobileStyles.fontStyle,
      fontWeight: entity.mobileStyles.fontWeight,
    },
    {
      fontFamily: entity.options.subTextFontFamily,
      fontStyle: entity.options.subTextFontStyle,
      fontWeight: entity.options.subTextFontWeight,
    },
    {
      fontFamily: entity.mobileOptions.subTextFontFamily,
      fontStyle: entity.mobileOptions.subTextFontStyle,
      fontWeight: entity.mobileOptions.subTextFontWeight,
    },
  ].filter(fontProperty => Boolean(fontProperty.fontFamily))
}

function getBreadcrumbsFontProperties(entity: BreadcrumbsInterface) {
  const fontProperties = entity.fontFamily
    ? [
        {
          fontFamily: entity.fontFamily,
          fontStyle: entity.fontStyle,
          fontWeight: entity.fontWeight,
        },
      ]
    : []

  if (entity.mobileFontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileFontFamily,
      fontStyle: entity.mobileFontStyle,
      fontWeight: entity.mobileFontWeight,
    })
  }

  return fontProperties
}

export function getGoogleFontProperties(
  entity:
    | OldEntityInterface
    | BaseEntityInterface
    | BaseEntityNewInterface
    | EntityNewInterfacesType,
): GoogleFontProperties[] {
  switch (entity.type) {
    case EntityTypeEnum.ContentTable:
      return getContentTableFontProperties(entity as ContentTableInterface)
    case structureTypes.BUTTON:
    case structureTypes.PAYMENT_BUTTON:
      return getButtonFontProperties(entity as OldEntityInterface)
    case EntityTypeEnum.Faq:
      return getFaqFontProperties(entity as FaqInterface)
    case EntityTypeEnum.Text:
      return getNewTextFontProperties(entity as TextInterface)
    case EntityTypeEnum.Headline:
      return getNewTextFontProperties(entity as TextInterface)
    case EntityTypeEnum.OfferPrice:
      return getOfferPriceFontProperties(entity as OfferPriceInterface)
    case EntityTypeEnum.Breadcrumbs:
    case EntityTypeEnum.BlogPostDate:
    case EntityTypeEnum.BlogPostCategories:
    case EntityTypeEnum.BlogPostTitle:
    case EntityTypeEnum.Menu:
    case EntityTypeEnum.NavigationMenu:
    case EntityTypeEnum.WebsiteLogo:
    case EntityTypeEnum.Countdown:
    case EntityTypeEnum.LanguageSwitcher:
    case EntityTypeEnum.WebinarSessionDateTime:
    case EntityTypeEnum.Product:
    case EntityTypeEnum.BlogCategoryTitle:
    case EntityTypeEnum.BlogCategoryDescription:
    case EntityTypeEnum.CustomLoginForgotPassword:
    case EntityTypeEnum.PaymentMethod:
    case EntityTypeEnum.Checkbox:
    case EntityTypeEnum.PriceElement:
    case EntityTypeEnum.OrderBump:
    case EntityTypeEnum.WebsiteLanguageSwitcher:
      return getBreadcrumbsFontProperties(entity as BreadcrumbsInterface)
    case EntityTypeEnum.BulletList:
      return getNewTextFontProperties(entity as TextInterface)
    case EntityTypeEnum.ContentBox:
      return getContentBoxFontProperties(entity as ContentBoxInterface)
    case EntityTypeEnum.Button:
    case EntityTypeEnum.PaymentButton:
    case EntityTypeEnum.UpsellAgreeButton:
    case EntityTypeEnum.UpsellDisagreeButton:
    case EntityTypeEnum.WebinarCallToAction:
    case EntityTypeEnum.SalesRedirectionButton:
      return getNewButtonFontProperties(entity as CommonButtonEntityInterface)
    case EntityTypeEnum.Survey:
      return getSurveyFontProperties(entity as SurveyInterface)
    case EntityTypeEnum.BlogPostListing:
    case EntityTypeEnum.LatestBlogPosts:
      return getNewBlogPostListingFontProperties(
        entity as BlogPostListingInterface | LatestBlogPostsInterface,
      )
    case EntityTypeEnum.SearchResults:
      return getSearchResultsFontProperties(entity as SearchResultsInterface)
    case EntityTypeEnum.Search:
      return getSearchFontProperties(entity as SearchInterface)
    case EntityTypeEnum.Form:
      return getFormFontProperties(entity as FormInterface)
    default:
      // todo ensure we always have old entity here
      return getBaseOldEntityFontProperties(entity)
  }
}

export const isGoogleFont = (fontFamily: string) => {
  return fontFamilies.google.includes(fontFamily)
}

export const appendGoogleFonts = (googleFontsStr: string) => {
  const newStyle = createFontFace(googleFontsStr)
  newStyle.setAttribute('google-font', 'pending')

  if (newStyle) {
    document.head.appendChild(newStyle)
  }

  const previousLinks = document.querySelectorAll('style[google-font=true]')
  //this time need to load fonts from newStyle then we remove old style to avoid a glitch
  setTimeout(() => {
    previousLinks.forEach(style => {
      style.remove()
    })
  }, 500)
  //find current style and change its attribute, so we can delete it next time we change the style
  const currentLink = document.querySelector('style[google-font=pending]')
  if (currentLink) {
    currentLink.setAttribute('google-font', 'true')
  }
}

export const entitiesWithFontOptions = [
  EntityTypeEnum.BlogPostListing,
  EntityTypeEnum.LatestBlogPosts,
  EntityTypeEnum.BlogPostTitle,
  EntityTypeEnum.WebinarCallToAction,
  EntityTypeEnum.WebinarSessionDateTime,
  EntityTypeEnum.BulletList,
  structureTypes.START_TIMER,
  EntityTypeEnum.Menu,
  EntityTypeEnum.NavigationMenu,
  EntityTypeEnum.WebsiteLogo,
  structureTypes.BUTTON,
  structureTypes.PAYMENT_BUTTON,
  EntityTypeEnum.PaymentMethod,
  EntityTypeEnum.SalesRedirectionButton,
  EntityTypeEnum.OrderBump,
  EntityTypeEnum.Faq,
  EntityTypeEnum.Text,
  EntityTypeEnum.Headline,
  EntityTypeEnum.ContentTable,
  EntityTypeEnum.Breadcrumbs,
  EntityTypeEnum.Button,
  EntityTypeEnum.ContactUsButton,
  EntityTypeEnum.PaymentButton,
  EntityTypeEnum.BlogPostDate,
  EntityTypeEnum.UpsellAgreeButton,
  EntityTypeEnum.UpsellDisagreeButton,
  EntityTypeEnum.BlogPostCategories,
  EntityTypeEnum.Survey,
  EntityTypeEnum.Product,
  EntityTypeEnum.Countdown,
  EntityTypeEnum.OfferPrice,
  EntityTypeEnum.CustomLoginForgotPassword,
  EntityTypeEnum.BlogCategoryTitle,
  EntityTypeEnum.BlogCategoryDescription,
  EntityTypeEnum.ContentBox,
  EntityTypeEnum.LanguageSwitcher,
  EntityTypeEnum.Checkbox,
  EntityTypeEnum.PriceElement,
  EntityTypeEnum.Form,
  EntityTypeEnum.Search,
  EntityTypeEnum.SearchResults,
  EntityTypeEnum.WebsiteLanguageSwitcher,
]

export function readFontFile(buffer: Blob) {
  return new Promise<Record<string, string> | undefined>(resolve => {
    const reader = new FileReader()
    reader.onload = e => {
      if (e.target) {
        const buffer = e.target.result
        try {
          const fontMeta = FontName.parse(buffer)[0]
          resolve(fontMeta)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
          resolve(undefined)
          // FontName may throw an Error
        }
      }
    }
    reader.readAsArrayBuffer(buffer)
  })
}

export interface Font {
  id: number
  fontFamily: string
  fontWeight: string
  fullName: string
  filePath: string
  fontLabel: string
  originalFullName: string
}

export type Fonts = Font[]

export function getFontDataWithActualFontLabel(font: Font, fontList: Font[]) {
  let fontLabel = font.originalFullName
  let countPostfix = 0
  fontList.forEach((curr: Font) => {
    if (curr.originalFullName === fontLabel) {
      countPostfix++
    }
  })
  if (countPostfix > 0) {
    fontLabel += ` (${countPostfix})`
  }
  return {
    ...font,
    fontLabel,
  }
}

export const isFontContainBoldWeight = (fontFamily?: string) => {
  return googleFontsList.find(
    font => font.family === fontFamily && font.variants.includes('700'),
  )
}

export async function getFontPropertiesFromFontFiles(fontFiles: DataFile[]) {
  const objectCounter: Record<string, number> = {}
  const result = await fontFiles.reduce(
    async (acc: Promise<Fonts>, curr: DataFile) => {
      const previousAccumulator = await acc
      try {
        const response = await axios.get(curr.path, { responseType: 'blob' })
        const metaData: Record<string, string> | undefined = await readFontFile(
          response.data,
        )
        if (metaData) {
          const originalFontName = getFilteredFontName(metaData.fullName)
          let fontLabel = originalFontName

          if (objectCounter[originalFontName]) {
            fontLabel += ` (${objectCounter[fontLabel]})`
            objectCounter[originalFontName] += 1
          } else {
            objectCounter[originalFontName] = 1
          }

          const fontData = {
            id: curr.id,
            fontFamily: metaData.fontFamily,
            fontWeight: metaData.fontSubfamily,
            fullName: getFilteredFontName(metaData.fullName) + `${curr.id}`,
            originalFullName: getFilteredFontName(metaData.fullName),
            fontLabel,
            filePath: curr.path,
          }
          return [...previousAccumulator, fontData]
        } else return previousAccumulator
      } catch (_e) {
        return previousAccumulator
      }
    },
    Promise.resolve([]),
  )
  return result
}

export async function validateRawCustomFontFile(file: File, mimeType: string) {
  const metaData: Record<string, string> | undefined = await readFontFile(
    new Blob([file], { type: mimeType }),
  )
  return !!metaData
}

const testStringOrNumberRegExp = /[a-zA-Z]+|[0-9]+|-|_/

export function getFilteredFontName(fontName: string) {
  const arrayWithoutSpaces = fontName.replace(/\s+/g, '').split('')
  // biome-ignore lint/suspicious/useIterableCallbackReturn: <explanation>
  const resArray = arrayWithoutSpaces.map(character => {
    if (testStringOrNumberRegExp.test(character)) {
      return character
    } else if (character.charCodeAt(0) < 160) {
      return ''
    }
  })

  return resArray.join('')
}

const inheritFontFamilyValue = 'inherit'

export function getClearEntityWithCustomFontProperties(
  entity: OldEntityInterface | EntityInterface | EntityNewInterfacesType,
  fileId: number,
): OldEntityInterface | EntityInterface | EntityNewInterfacesType {
  switch (entity.type) {
    case EntityTypeEnum.ContentTable: {
      const typedEntity = entity as ContentTableInterface
      if (typedEntity.headerFontFileId === fileId) {
        typedEntity.headerFontFileId = undefined
        typedEntity.headerFontFamily = inheritFontFamilyValue
      }
      if (typedEntity.contentFontFileId === fileId) {
        typedEntity.contentFontFileId = undefined
        typedEntity.contentFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case structureTypes.BUTTON:
    case structureTypes.PAYMENT_BUTTON: {
      const typedEntity = entity as OldEntityInterface
      if (typedEntity.options.textFontFileId === fileId) {
        typedEntity.options.textFontFileId = undefined
        typedEntity.styles.fontFamily = inheritFontFamilyValue
      }
      if (typedEntity.mobileOptions.textFontFileId === fileId) {
        typedEntity.mobileOptions.textFontFileId = undefined
        typedEntity.mobileStyles.fontFamily = inheritFontFamilyValue
      }
      if (typedEntity.options.subTextFontFileId === fileId) {
        typedEntity.options.subTextFontFileId = undefined
        typedEntity.options.subTextFontFamily = inheritFontFamilyValue
      }
      if (typedEntity.mobileOptions.subTextFontFileId === fileId) {
        typedEntity.mobileOptions.subTextFontFileId = undefined
        typedEntity.mobileOptions.subTextFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case EntityTypeEnum.ContentBox: {
      const typedEntity = entity as ContentBoxInterface
      if (typedEntity.header.fontFileId === fileId) {
        typedEntity.header.fontFileId = undefined
        typedEntity.header.fontFamily = inheritFontFamilyValue
      }
      if (typedEntity.header.mobileFontFileId === fileId) {
        typedEntity.header.mobileFontFileId = undefined
        typedEntity.header.mobileFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case EntityTypeEnum.Faq: {
      const typedEntity = entity as FaqInterface
      if (typedEntity.fontFileId === fileId) {
        typedEntity.fontFileId = undefined
        typedEntity.titleFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case EntityTypeEnum.Button:
    case EntityTypeEnum.PaymentButton:
    case EntityTypeEnum.UpsellAgreeButton:
    case EntityTypeEnum.UpsellDisagreeButton:
    case EntityTypeEnum.WebinarCallToAction:
    case EntityTypeEnum.SalesRedirectionButton: {
      const typedEntity = entity as CommonButtonEntityInterface
      if (typedEntity.textFontFileId === fileId) {
        typedEntity.textFontFileId = undefined
        typedEntity.textFontFamily = inheritFontFamilyValue
      }
      if (typedEntity.mobileTextFontFileId === fileId) {
        typedEntity.mobileTextFontFileId = undefined
        typedEntity.mobileTextFontFamily = inheritFontFamilyValue
      }
      if (typedEntity.subTextFontFileId === fileId) {
        typedEntity.subTextFontFileId = undefined
        typedEntity.subTextFontFamily = inheritFontFamilyValue
      }
      if (typedEntity.mobileSubTextFontFileId === fileId) {
        typedEntity.mobileSubTextFontFileId = undefined
        typedEntity.mobileSubTextFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case EntityTypeEnum.Breadcrumbs:
    case EntityTypeEnum.BlogPostDate:
    case EntityTypeEnum.BlogPostCategories:
    case EntityTypeEnum.BlogPostTitle:
    case EntityTypeEnum.Menu:
    case EntityTypeEnum.NavigationMenu:
    case EntityTypeEnum.WebsiteLogo:
    case EntityTypeEnum.WebinarSessionDateTime:
    case EntityTypeEnum.Product:
    case EntityTypeEnum.Text:
    case EntityTypeEnum.Headline:
    case EntityTypeEnum.Survey:
    case EntityTypeEnum.LanguageSwitcher:
    case EntityTypeEnum.WebsiteLanguageSwitcher:
    case EntityTypeEnum.Countdown:
    case EntityTypeEnum.Checkbox: {
      const typedEntity = entity as TextInterface
      if (typedEntity.fontFileId === fileId) {
        typedEntity.fontFileId = undefined
        typedEntity.fontFamily = inheritFontFamilyValue
      }
      if (typedEntity.mobileFontFileId === fileId) {
        typedEntity.mobileFontFileId = undefined
        typedEntity.mobileFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    case EntityTypeEnum.BlogPostListing:
    case EntityTypeEnum.LatestBlogPosts: {
      const typedEntity = entity as BlogPostListingInterface
      if (typedEntity.titleFontFileId === fileId) {
        typedEntity.titleFontFileId = undefined
        typedEntity.titleFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileTitleFontFileId === fileId) {
        typedEntity.mobileTitleFontFileId = undefined
        typedEntity.mobileTitleFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.categoriesFontFileId === fileId) {
        typedEntity.categoriesFontFileId = undefined
        typedEntity.categoriesFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileCategoriesFontFileId === fileId) {
        typedEntity.mobileCategoriesFontFileId = undefined
        typedEntity.mobileCategoriesFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.descriptionFontFileId === fileId) {
        typedEntity.descriptionFontFileId = undefined
        typedEntity.descriptionFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileDescriptionFontFileId === fileId) {
        typedEntity.mobileDescriptionFontFileId = undefined
        typedEntity.mobileDescriptionFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.dateFontFileId === fileId) {
        typedEntity.dateFontFileId = undefined
        typedEntity.dateFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileDateFontFileId === fileId) {
        typedEntity.mobileDateFontFileId = undefined
        typedEntity.mobileDateFontFamily = inheritFontFamilyValue
      }

      return typedEntity
    }
    case EntityTypeEnum.OfferPrice: {
      const typedEntity = entity as OfferPriceInterface

      if (typedEntity.nameFontFileId === fileId) {
        typedEntity.nameFontFileId = undefined
        typedEntity.nameFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileNameFontFileId === fileId) {
        typedEntity.mobileNameFontFileId = undefined
        typedEntity.mobileNameFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.descriptionFontFileId === fileId) {
        typedEntity.descriptionFontFileId = undefined
        typedEntity.descriptionFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileDescriptionFontFileId === fileId) {
        typedEntity.mobileDescriptionFontFileId = undefined
        typedEntity.mobileDescriptionFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.amountFontFileId === fileId) {
        typedEntity.amountFontFileId = undefined
        typedEntity.amountFontFamily = inheritFontFamilyValue
      }

      if (typedEntity.mobileAmountFontFileId === fileId) {
        typedEntity.mobileAmountFontFileId = undefined
        typedEntity.mobileAmountFontFamily = inheritFontFamilyValue
      }

      return typedEntity
    }
    case EntityTypeEnum.Form: {
      const typedEntity = entity as FormInterface
      const buttonCommonStyles = typedEntity.buttonCommonStyles
      const checkboxesCommonStyles = typedEntity.checkboxesCommonStyles

      if (buttonCommonStyles.textFontFileId === fileId) {
        buttonCommonStyles.textFontFileId = undefined
        buttonCommonStyles.textFontFamily = inheritFontFamilyValue
      }
      if (buttonCommonStyles.mobileTextFontFileId === fileId) {
        buttonCommonStyles.mobileTextFontFileId = undefined
        buttonCommonStyles.mobileTextFontFamily = inheritFontFamilyValue
      }
      if (buttonCommonStyles.subTextFontFileId === fileId) {
        buttonCommonStyles.subTextFontFileId = undefined
        buttonCommonStyles.subTextFontFamily = inheritFontFamilyValue
      }
      if (buttonCommonStyles.mobileSubTextFontFileId === fileId) {
        buttonCommonStyles.mobileSubTextFontFileId = undefined
        buttonCommonStyles.mobileSubTextFontFamily = inheritFontFamilyValue
      }
      if (checkboxesCommonStyles.fontFileId === fileId) {
        checkboxesCommonStyles.fontFileId = undefined
        checkboxesCommonStyles.fontFamily = inheritFontFamilyValue
      }
      if (checkboxesCommonStyles.mobileFontFileId === fileId) {
        checkboxesCommonStyles.mobileFontFileId = undefined
        checkboxesCommonStyles.mobileFontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
    default: {
      const typedEntity = entity as OldEntityInterface
      if (typedEntity.options && typedEntity.options.fontFileId === fileId) {
        typedEntity.options.fontFileId = undefined
        typedEntity.styles.fontFamily = inheritFontFamilyValue
      }
      if (
        typedEntity.mobileOptions &&
        typedEntity.mobileOptions.fontFileId === fileId
      ) {
        typedEntity.mobileOptions.fontFileId = undefined
        typedEntity.mobileStyles.fontFamily = inheritFontFamilyValue
      }
      return typedEntity
    }
  }
}
